import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS as LMSEndpoints } from '../../../Utils/Endpoints';
import { ENDPOINTS as SecEndpoints } from '../../../../Seguridad/Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './AsignacionMasivaPuestoModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import { getSessionUserId } from '../../../../../utils/SessionUser';
import AlertDialog from '../../Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Asignación Masiva por Puesto";
const endpoint = LMSEndpoints.asignacionMasivaPuesto;
const endpointById = LMSEndpoints.asignacionMasivaPuestoById;
const modelo = Models.asignacionMasivaPuesto;

export default function Catalog() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [cursos, setCursos] = useState([]);
    const [puestos, setPuestos] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    const loadData = async () => {
        setLoading(true);
        try {
            const [resMain, resCursos, resPuestos] = await Promise.all([
                axios.get(endpoint),
                axios.get(LMSEndpoints.cursos),
                axios.get(SecEndpoints.puestos)
            ]);

            setCursos((resCursos.data || []).map(c => ({ idCurso: c.idCurso ?? c.IdCurso, nombre: c.nombre ?? c.Nombre })));
            setPuestos((resPuestos.data || []).map(p => ({ idPuesto: p.idPuesto ?? p.IdPuesto, nombre: p.nombre ?? p.Nombre })));
            setRows((resMain.data || []).map((x) => modelo(x)));
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar información');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const getCursoNombre = (id) => cursos.find(c => c.idCurso === id)?.nombre || `Curso ${id}`;
    const getPuestoNombre = (id) => puestos.find(p => p.idPuesto === id)?.nombre || `Puesto ${id}`;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const curso = getCursoNombre(r.idCurso).toLowerCase();
            const puesto = getPuestoNombre(r.idPuesto).toLowerCase();
            const fecha = (r.fechaLimite || '').toLowerCase();
            return curso.includes(q) || puesto.includes(q) || fecha.includes(q);
        });
    }, [rows, query, cursos, puestos]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (id) => {
        const r = rows.find(x => x.idAsignacionMasivaPuesto === id);
        if (!r) return;
        setSelectedId(id);
        setEditData(r);
        setModalMode('edit');
        setModalOpen(true);
    };

    const askDelete = (id) => {
        const r = rows.find(x => x.idAsignacionMasivaPuesto === id);
        setDeleteTarget({ id, name: `la asignación masiva para ${getCursoNombre(r.idCurso)}` });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (payload, mode) => {
        try {
            setLoading(true);
            const dataToSave = {
                ...payload,
                fechaLimite: payload.fechaLimite || null,
                idCurso: parseInt(payload.idCurso),
                idPuesto: parseInt(payload.idPuesto),
                idUsuarioJefe: getSessionUserId()
            };

            if (mode === 'new') {
                await axios.post(endpoint, dataToSave);
            } else {
                await axios.put(endpoint, dataToSave);
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >{label}</button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar por curso, puesto o fecha..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={handleNew}>+ Nuevo</button>
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Curso</th>
                                    <th>Puesto</th>
                                    <th>Fecha Límite</th>
                                    <th style={{ textAlign: 'center' }}>Editar</th>
                                    <th style={{ textAlign: 'center' }}>Borrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idAsignacionMasivaPuesto}>
                                        <td><strong>{getCursoNombre(r.idCurso)}</strong></td>
                                        <td>{getPuestoNombre(r.idPuesto)}</td>
                                        <td>{r.fechaLimite ? new Date(r.fechaLimite).toLocaleDateString() : '-'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button type="button" className="rk-iconbtn rk-iconbtn--edit" onClick={() => handleEdit(r.idAsignacionMasivaPuesto)} title="Editar">
                                                <img src={sigmaEdit} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button type="button" className="rk-iconbtn rk-iconbtn--del" onClick={() => askDelete(r.idAsignacionMasivaPuesto)} title="Borrar">
                                                <img src={sigmaDelete} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (<tr key="empty-row"><td colSpan={5}>Sin resultados.</td></tr>)}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        <span>{filtered.length} resultados</span>
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
                        {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
                        <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
